/**
 * QuotesListController — page /devis.html (EPIC-012 / 12.6).
 *
 * Hydrate la liste paginée des devis depuis `GET /quotes`. Le backend
 * enrichit chaque item avec `clientLegalName` (batch SELECT côté repository
 * Client pour éviter N+1).
 *
 * Filtres : statut, période [from..to] sur `date_created`, recherche libre
 * (`q` ILIKE sur `number` OU `legal_name` du client).
 *
 * Pagination 25/page (cohérent EPIC-009 clients), tri date d'émission DESC
 * (côté backend : `ORDER BY date_created DESC, id DESC`, pas de tri custom
 * exposé en V1).
 *
 * Action contextuelle par ligne :
 *  - statut `draft` → lien « Éditer » vers `/devis-edition.html?id={id}`
 *  - autres statuts → lien « Voir » vers `/devis-detail.html?id={id}`
 *
 * Convention CLAUDE.md respectée : pas d'HTML inline en JS (clone de
 * `<template>` + remplissage `data-slot` + setAttribute côté JS pour
 * l'attribut `href` / `datetime`). Aucune classe CSS inventée.
 */

import BaseController from "./base_controller.js";
import { api, APIError } from "/js/services/api.js";

const ORIGIN_STORAGE_KEY = "nm_filter_origin_devis";

const STATUS_LABELS = {
  draft: "brouillon",
  issued: "émis",
  sent: "envoyé",
  accepted: "accepté",
  refused: "refusé",
  partially_invoiced: "partiellement facturé",
  fully_invoiced: "facturé",
  expired: "expiré",
  archived: "archivé",
};

const STATUS_PILL_MODIFIER = {
  draft: "nm-pill--indigo",
  issued: "nm-pill--ochre",
  sent: "nm-pill--ochre",
  accepted: "nm-pill--olive",
  refused: "nm-pill--red",
  partially_invoiced: "nm-pill--blue",
  fully_invoiced: "nm-pill--blue",
  expired: "",
  archived: "",
};

export default class QuotesListController extends BaseController {
  static targets = [
    "tableWrap",
    "tableBody",
    "loadingRow",
    "summary",
    "searchInput",
    "statusFilter",
    "fromFilter",
    "toFilter",
    "originToggleGroup",
    "originToggle",
    "pagination",
    "pageInfo",
    "pageButtons",
  ];

  static values = {
    page: { type: Number, default: 1 },
    perPage: { type: Number, default: 25 },
    q: { type: String, default: "" },
    statusFilter: { type: String, default: "" },
    fromFilter: { type: String, default: "" },
    toFilter: { type: String, default: "" },
    originFilter: { type: String, default: "all" },
  };

  connect() {
    this._debounceTimer = null;
    this._fetchSeq = 0;
    this._restoreFromQueryString();
    this._restoreOriginFromSession();
    this._loadImportCutover();
    this.refresh();
  }

  disconnect() {
    if (this._debounceTimer) clearTimeout(this._debounceTimer);
  }

  // ---------------------------------------------------------------------------
  // Querystring persistence
  // ---------------------------------------------------------------------------

  _restoreFromQueryString() {
    const params = new URLSearchParams(window.location.search);
    const page = Number(params.get("page"));
    if (Number.isFinite(page) && page >= 1) this.pageValue = page;
    if (params.has("status")) {
      this.statusFilterValue = params.get("status") ?? "";
      if (this.hasStatusFilterTarget) this.statusFilterTarget.value = this.statusFilterValue;
    }
    if (params.has("from")) {
      this.fromFilterValue = params.get("from") ?? "";
      if (this.hasFromFilterTarget) this.fromFilterTarget.value = this.fromFilterValue;
    }
    if (params.has("to")) {
      this.toFilterValue = params.get("to") ?? "";
      if (this.hasToFilterTarget) this.toFilterTarget.value = this.toFilterValue;
    }
    if (params.has("q")) {
      this.qValue = params.get("q") ?? "";
      if (this.hasSearchInputTarget) this.searchInputTarget.value = this.qValue;
    }
  }

  _syncQueryString() {
    const params = new URLSearchParams();
    if (this.pageValue > 1) params.set("page", String(this.pageValue));
    if (this.statusFilterValue) params.set("status", this.statusFilterValue);
    if (this.fromFilterValue) params.set("from", this.fromFilterValue);
    if (this.toFilterValue) params.set("to", this.toFilterValue);
    if (this.qValue) params.set("q", this.qValue);
    const qs = params.toString();
    const newUrl = window.location.pathname + (qs ? `?${qs}` : "");
    window.history.replaceState(null, "", newUrl);
  }

  // ---------------------------------------------------------------------------
  // Filter / search / paginate
  // ---------------------------------------------------------------------------

  search(event) {
    if (this._debounceTimer) clearTimeout(this._debounceTimer);
    const value = event.target.value;
    this._debounceTimer = setTimeout(() => {
      this.qValue = value;
      this.pageValue = 1;
      this.refresh();
    }, 300);
  }

  changeStatus(event) {
    this.statusFilterValue = event.target.value;
    this.pageValue = 1;
    this.refresh();
  }

  changeFrom(event) {
    this.fromFilterValue = event.target.value;
    this.pageValue = 1;
    this.refresh();
  }

  changeTo(event) {
    this.toFilterValue = event.target.value;
    this.pageValue = 1;
    this.refresh();
  }

  paginate(event) {
    const page = Number(event.currentTarget.dataset.page);
    if (!Number.isFinite(page) || page < 1) return;
    this.pageValue = page;
    this.refresh();
  }

  reset() {
    this.statusFilterValue = "";
    this.fromFilterValue = "";
    this.toFilterValue = "";
    this.qValue = "";
    this.originFilterValue = "all";
    this.pageValue = 1;
    if (this.hasStatusFilterTarget) this.statusFilterTarget.value = "";
    if (this.hasFromFilterTarget) this.fromFilterTarget.value = "";
    if (this.hasToFilterTarget) this.toFilterTarget.value = "";
    if (this.hasSearchInputTarget) this.searchInputTarget.value = "";
    if (this.hasOriginToggleTarget) this.originToggleTarget.checked = true;
    this._persistOriginToSession();
    this.refresh();
  }

  changeOrigin(event) {
    this.originFilterValue = event.target.checked ? "all" : "native";
    this._persistOriginToSession();
    this.pageValue = 1;
    this.refresh();
  }

  _restoreOriginFromSession() {
    try {
      const stored = window.sessionStorage.getItem(ORIGIN_STORAGE_KEY);
      if (stored === "native" || stored === "all") {
        this.originFilterValue = stored;
        if (this.hasOriginToggleTarget) this.originToggleTarget.checked = stored === "all";
      }
    } catch {
      // sessionStorage indisponible — fallback "all".
    }
  }

  _persistOriginToSession() {
    try {
      window.sessionStorage.setItem(ORIGIN_STORAGE_KEY, this.originFilterValue);
    } catch {
      // sessionStorage indisponible — fail silencieux.
    }
  }

  async _loadImportCutover() {
    if (!this.hasOriginToggleGroupTarget) return;
    try {
      const profile = await api.getOrganizationProfile();
      const cutover = profile?.importCutoverDate ?? null;
      this.originToggleGroupTarget.hidden = !cutover;
    } catch {
      this.originToggleGroupTarget.hidden = true;
    }
  }

  // ---------------------------------------------------------------------------
  // Fetch + render
  // ---------------------------------------------------------------------------

  async refresh() {
    const seq = ++this._fetchSeq;
    if (this.hasTableWrapTarget) this.tableWrapTarget.setAttribute("aria-busy", "true");
    this._syncQueryString();

    const params = { page: this.pageValue, perPage: this.perPageValue };
    if (this.qValue) params.q = this.qValue;
    if (this.statusFilterValue) params.status = this.statusFilterValue;
    if (this.fromFilterValue) params.from = this.fromFilterValue;
    if (this.toFilterValue) params.to = this.toFilterValue;
    if (this.originFilterValue && this.originFilterValue !== "all") params.origin = this.originFilterValue;

    try {
      const response = await api.listQuotes(params);
      if (seq !== this._fetchSeq) return;
      const quotes = Array.isArray(response?.data) ? response.data : [];
      const pagination = response?.pagination ?? { page: 1, perPage: this.perPageValue, total: 0, totalPages: 1 };
      this._renderRows(quotes);
      this._renderSummary(pagination);
      this._renderPagination(pagination);
    } catch (error) {
      if (seq !== this._fetchSeq) return;
      this._renderError();
      const msg = error instanceof APIError && error.message
        ? error.message
        : "Impossible de charger les devis.";
      this.showError(msg);
    } finally {
      if (seq === this._fetchSeq && this.hasTableWrapTarget) {
        this.tableWrapTarget.setAttribute("aria-busy", "false");
      }
    }
  }

  _renderRows(quotes) {
    if (!this.hasTableBodyTarget) return;
    this._clearTableBody();

    if (quotes.length === 0) {
      this.tableBodyTarget.appendChild(this.cloneTemplate("tpl-quote-empty"));
      return;
    }

    for (const quote of quotes) {
      const fragment = this.cloneTemplate("tpl-quote-row");
      const detailUrl = `/devis-detail.html?id=${encodeURIComponent(quote.id)}`;
      const editUrl = `/devis-edition.html?id=${encodeURIComponent(quote.id)}`;
      const isDraft = quote.status === "draft";

      this.fillSlots(fragment, {
        number: quote.number ?? "—",
        clientName: quote.clientLegalName ?? "—",
        dateCreated: this.formatDate(quote.dateCreated),
        dateValidity: quote.dateValidity ? this.formatDate(quote.dateValidity) : "—",
        amountExclVat: this._formatAmount(quote.amountExclVat),
        statusLabel: STATUS_LABELS[quote.status] ?? quote.status,
        actionText: isDraft ? "Éditer" : "Voir",
      });

      const clientLink = fragment.querySelector('[data-slot-attr-href="detailUrl"]');
      if (clientLink) clientLink.setAttribute("href", detailUrl);

      const actionLink = fragment.querySelector('[data-slot-attr-href="actionUrl"]');
      if (actionLink) {
        actionLink.setAttribute("href", isDraft ? editUrl : detailUrl);
        actionLink.setAttribute(
          "aria-label",
          isDraft
            ? `Éditer le devis ${quote.number ?? "brouillon"}`
            : `Voir le devis ${quote.number ?? quote.id}`,
        );
      }

      const dateCreatedEl = fragment.querySelector('[data-slot-attr-datetime="dateCreatedIso"]');
      if (dateCreatedEl && quote.dateCreated) {
        dateCreatedEl.setAttribute("datetime", quote.dateCreated);
      }
      const dateValidityEl = fragment.querySelector('[data-slot-attr-datetime="dateValidityIso"]');
      if (dateValidityEl && quote.dateValidity) {
        dateValidityEl.setAttribute("datetime", quote.dateValidity);
      }

      const pill = fragment.querySelector('[data-slot-element="statusPill"]');
      const modifier = STATUS_PILL_MODIFIER[quote.status];
      if (pill && modifier) pill.classList.add(modifier);

      const importedBadge = fragment.querySelector('[data-slot-element="importedBadge"]');
      if (importedBadge && quote.origin === "imported") importedBadge.hidden = false;

      if (quote.status === "archived") {
        const tr = fragment.querySelector("tr");
        if (tr) tr.classList.add("is-archived");
      }

      this.tableBodyTarget.appendChild(fragment);
    }
  }

  _renderSummary(pagination) {
    if (!this.hasSummaryTarget) return;
    const total = Number(pagination.total) || 0;
    this.summaryTarget.textContent = total === 0
      ? "Aucun devis"
      : `${total} devis`;
  }

  _renderPagination(pagination) {
    if (!this.hasPaginationTarget || !this.hasPageButtonsTarget || !this.hasPageInfoTarget) {
      return;
    }
    const page = Number(pagination.page) || 1;
    const perPage = Number(pagination.perPage) || this.perPageValue;
    const total = Number(pagination.total) || 0;
    const totalPages = Number(pagination.totalPages) || 1;

    if (total === 0 || totalPages <= 1) {
      this.paginationTarget.hidden = true;
      return;
    }
    this.paginationTarget.hidden = false;

    const start = (page - 1) * perPage + 1;
    const end = Math.min(page * perPage, total);
    this.pageInfoTarget.textContent = `${start} — ${end} sur ${total} devis`;

    while (this.pageButtonsTarget.firstChild) {
      this.pageButtonsTarget.removeChild(this.pageButtonsTarget.firstChild);
    }
    this.pageButtonsTarget.appendChild(this._buildPageButton({
      page: page - 1, text: "‹", label: "Page précédente", disabled: page <= 1,
    }));
    for (const token of this.getPagesToShow(page, totalPages)) {
      if (token === "...") {
        this.pageButtonsTarget.appendChild(this.cloneTemplate("tpl-pagination-ellipsis"));
        continue;
      }
      this.pageButtonsTarget.appendChild(this._buildPageButton({
        page: token,
        text: String(token),
        label: `Page ${token}`,
        current: token === page,
      }));
    }
    this.pageButtonsTarget.appendChild(this._buildPageButton({
      page: page + 1, text: "›", label: "Page suivante", disabled: page >= totalPages,
    }));
  }

  _buildPageButton({ page, text, label, current = false, disabled = false }) {
    const fragment = this.cloneTemplate("tpl-pagination-page");
    const btn = fragment.querySelector("button");
    if (!btn) return fragment;
    btn.textContent = text;
    btn.setAttribute("aria-label", label);
    btn.dataset.page = String(page);
    if (disabled) btn.disabled = true;
    if (current) {
      btn.classList.add("is-current");
      btn.setAttribute("aria-current", "page");
    }
    return fragment;
  }

  _renderError() {
    if (!this.hasTableBodyTarget) return;
    this._clearTableBody();
    this.tableBodyTarget.appendChild(this.cloneTemplate("tpl-quote-error"));
    if (this.hasPaginationTarget) this.paginationTarget.hidden = true;
    if (this.hasSummaryTarget) this.summaryTarget.textContent = "Erreur de chargement";
  }

  _clearTableBody() {
    while (this.tableBodyTarget.firstChild) {
      this.tableBodyTarget.removeChild(this.tableBodyTarget.firstChild);
    }
  }

  _formatAmount(raw) {
    if (raw === null || raw === undefined || raw === "") return "—";
    const num = Number(raw);
    if (!Number.isFinite(num)) return raw;
    return num.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
}
